import { ArrowRightOutlined } from '@ant-design/icons'
import { useAuth } from '@app/icare/client/auth'
import { db } from '@app/icare/client/db'
import {
  localPaginatedQueryOptions,
  type ResultTypeFromQueryResult,
} from '@app/icare/client/db/client/localQueryOptions'
import { relation } from '@app/icare/client/db/helpers'
import { queryKit } from '@app/icare/db/query/queryKit'
import { PatientStatusEnum } from '@app/icare/entities/patient/schema/Patient'
import type { PatientFindingGroup, PatientFindingView } from '@app/icare/entities/patientFinding/schema/PatientFinding'
import {
  PATIENT_FINDINGS_CONFIG,
  type PatientFindingConfigKey,
} from '@app/icare/entities/patientFinding/schema/PatientFinding.config'
import { t } from '@lingui/core/macro'
import { useLingui } from '@lingui/react'
import { Trans } from '@lingui/react/macro'
import { createQueryStore, F, Q, QueryBaseSchema, useColumnDef } from '@pkg/api/query'
import { ButtonLink, FilterButton } from '@pkg/ui/button'
import { LinkIndicatorIcon, SearchInput } from '@pkg/ui/common'
import { FormattedPatientNameAndAddress } from '@pkg/ui/formattedContent'
import { usePageContainerOverride } from '@pkg/ui/layout/PageContainer'
import { QueryFilterWrapper } from '@pkg/ui/query/QueryFilterWrapper'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Divider, Space, Table, type TableProps, Typography } from 'antd'
import { sql } from 'kysely'
import { useState } from 'react'
import { z } from 'zod'
import { useShallow } from 'zustand/react/shallow'

export const Route = createFileRoute('/_app/_dashboards/missing-data')({
  component: RouteComponent,
})

const FilterSchema = QueryBaseSchema.extend({
  sortBy: z.enum(['patient.personal.lastName', 'findings.count']).default('patient.personal.lastName').nullish(),
  'patient.status.value': Q.in(z.enum(PatientStatusEnum.$.zodEnum())).nullish(),
})

const INITIAL_FILTERS = {
  'patient.status.value': F.in([PatientStatusEnum.new, PatientStatusEnum.open]),
}

const useMissingDataQueryStore = createQueryStore({
  schema: FilterSchema,
  initialState: {
    filters: INITIAL_FILTERS,
  },
})

interface FindingProblem {
  key: string
  message: string
  route?: string
}

interface GroupedFindingRow {
  key: string
  section: string
  count: number
  problems: FindingProblem[]
}

const GROUP_ROUTE_SUFFIXES: Record<string, string> = {
  personal: '',
  address: '',
  addresses: 'lieferadressen',
  relatives: 'stammdaten/angehoerige',
  separateResidences: 'aufenthaltsorte',
  anamnesis: 'anamnese',
  therapies: 'therapien',
  costCarriers: 'kostentraeger',
  pharmacies: 'apotheken',
  forms: 'stammblatt',
  doctors: 'praxen',
  clinics: 'kliniken',
  careServices: 'pflegedienste',
  medicalTechnologies: 'medizintechnikversorger',
  adTeams: 'zustandigkeit',
  careManagers: 'zustandigkeit',
  cooperationPartners: 'kooperationspartner',
  applicants: 'anmelder',
}

const FINDING_ROUTE_SUFFIXES: Partial<Record<PatientFindingConfigKey, string>> = {
  FormsPatientMasterDataSheetRequired: 'stammblatt',
  FormsPatientConsentAndConfidentialityRequired: 'unterschriftsdokumente/einwilligung-schweigepflicht',
  FormsPatientSupplyRequired: 'unterschriftsdokumente/versorgungsauftrag',
  FormsPatientInformationRequired: 'unterschriftsdokumente/patienteninformationen',
}

const getPatientRoute = (patientId: string, group: PatientFindingGroup): string | undefined => {
  const suffix = GROUP_ROUTE_SUFFIXES[group]

  if (suffix === undefined) {
    return undefined
  }

  return suffix === '' ? `/patienten/${patientId}` : `/patienten/${patientId}/${suffix}`
}

const getPatientRouteForFinding = (
  patientId: string,
  group: PatientFindingGroup,
  findingId: PatientFindingConfigKey,
): string | undefined => {
  const specificSuffix = FINDING_ROUTE_SUFFIXES[findingId]

  if (specificSuffix !== undefined) {
    return specificSuffix === '' ? `/patienten/${patientId}` : `/patienten/${patientId}/${specificSuffix}`
  }

  return getPatientRoute(patientId, group)
}

function RouteComponent() {
  const { i18n } = useLingui()
  const { current } = useAuth()
  const queryStore = useMissingDataQueryStore(useShallow(s => s))
  const [open, setOpen] = useState(false)

  type MissingDataFinding = Pick<PatientFindingView, 'patientId' | 'findingId' | 'findingGroup'>
  const patientFindingCountsQuery = db
    .selectFrom('patientFindingsView')
    .select(['patientFindingsView.patientId', sql<number>`count(*)`.as('findingsCount')])
    .groupBy('patientFindingsView.patientId')
    .as('patientFindingCounts')

  const missingDataBaseQuery = db
    .selectFrom('patient')
    .innerJoin(patientFindingCountsQuery, 'patientFindingCounts.patientId', 'patient.id')
    .selectAll('patient')

  const missingDataQuery = missingDataBaseQuery.$if(!!current?.id, qb =>
    qb.where(({ exists, selectFrom }) =>
      exists(
        selectFrom('patientCareManager')
          .select('patientCareManager.id')
          .whereRef('patientCareManager.patientId', '=', 'patient.id')
          .$call(relation.isActive('patientCareManager'))
          .where(sql<boolean>`${sql.ref('patientCareManager.toId')} = ${current?.id}`),
      ),
    ),
  )

  const missingDataParams = queryKit.parseQuery(queryStore, { paginationMode: 'offset' })
  const isFindingsCountSort = missingDataParams.sortBy === 'findings.count'
  const isPatientLastNameSort = missingDataParams.sortBy === 'patient.personal.lastName'
  const missingDataBaseParams = {
    ...missingDataParams,
    sortBy: isFindingsCountSort || isPatientLastNameSort ? null : (missingDataParams.sortBy ?? null),
  }

  const { data, isFetching } = useQuery(
    localPaginatedQueryOptions({
      query: missingDataQuery
        .$if(isFindingsCountSort, qb => qb.orderBy('patientFindingCounts.findingsCount', missingDataParams.sortDir))
        .$if(isPatientLastNameSort, qb =>
          qb.orderBy(sql`lower(json_extract(patient.personal, '$.lastName'))`, missingDataParams.sortDir),
        )
        .$call(queryKit.all(missingDataBaseParams)),
      params: missingDataParams,
    }),
  )

  const patientIds = data?.data?.map(patient => patient.id) ?? []

  const { data: findings = [], isFetching: isFetchingFindings } = useQuery<MissingDataFinding[]>({
    queryKey: ['missing-data', patientIds.join(','), data?.data?.map(patient => patient.updatedAt).join(',')],
    queryFn: () => {
      if (patientIds.length === 0) {
        return []
      }

      return db
        .selectFrom('patientFindingsView')
        .select(['patientId', 'findingId', 'findingGroup'])
        .where('patientId', 'in', patientIds)
        .execute()
    },
    enabled: patientIds.length > 0,
  })

  type PatientRow = ResultTypeFromQueryResult<typeof data>
  interface MissingDataRow {
    id: PatientRow['id']
    patient: PatientRow
    findings: MissingDataFinding[]
  }

  const groupLabels: Record<string, string> = {
    personal: i18n._(t`Stammdaten`),
    address: i18n._(t`Stammdaten`),
    addresses: i18n._(t`Stammdaten`),
    relatives: i18n._(t`Angehörige`),
    separateResidences: i18n._(t`gesonderte Aufenthaltsorte`),
    anamnesis: i18n._(t`Anamnese`),
    therapies: i18n._(t`Therapien`),
    costCarriers: i18n._(t`Kostenträger`),
    pharmacies: i18n._(t`Apotheken`),
    forms: i18n._(t`Dokumente`),
    doctors: i18n._(t`Praxen`),
    clinics: i18n._(t`Kliniken`),
    careServices: i18n._(t`Pflegedienste`),
    medicalTechnologies: i18n._(t`Medizintechnik`),
    adTeams: i18n._(t`Zuständigkeit`),
    careManagers: i18n._(t`Zuständigkeit`),
    cooperationPartners: i18n._(t`Kooperationspartner`),
    applicants: i18n._(t`Anmelder`),
    deceased: i18n._(t`Verstorben`),
  }

  const groupFindings = (patientId: string, patientFindings: MissingDataFinding[]): GroupedFindingRow[] => {
    const grouped = patientFindings.reduce<
      Record<string, { group: PatientFindingGroup; findingIds: PatientFindingConfigKey[] }>
    >((acc, finding) => {
      if (!acc[finding.findingGroup]) {
        acc[finding.findingGroup] = {
          group: finding.findingGroup,
          findingIds: [],
        }
      }

      const findingId = finding.findingId as PatientFindingConfigKey
      if (!acc[finding.findingGroup]?.findingIds.includes(findingId)) {
        acc[finding.findingGroup]?.findingIds.push(findingId)
      }

      return acc
    }, {})

    return Object.values(
      Object.values(grouped)
        .map(entry => {
          const count = patientFindings.filter(f => f.findingGroup === entry.group).length
          const section = groupLabels[entry.group] ?? entry.group

          return {
            key: section,
            section,
            count,
            problems: entry.findingIds.map(findingId => ({
              key: findingId,
              message: i18n._(PATIENT_FINDINGS_CONFIG[findingId].message),
              route: getPatientRouteForFinding(patientId, entry.group, findingId),
            })),
          }
        })
        .reduce<Record<string, GroupedFindingRow>>((acc, row) => {
          const existing = acc[row.key]

          if (existing) {
            acc[row.key] = {
              key: existing.key,
              section: existing.section,
              count: existing.count + row.count,
              problems: [...existing.problems, ...row.problems],
            }
          } else {
            acc[row.key] = row
          }

          return acc
        }, {}),
    ).sort((left, right) => right.count - left.count)
  }

  const findingsByPatientId = findings.reduce((acc, finding) => {
    const patientFindings = acc.get(finding.patientId)
    if (patientFindings) {
      patientFindings.push(finding)
    } else {
      acc.set(finding.patientId, [finding])
    }

    return acc
  }, new Map<PatientRow['id'], MissingDataFinding[]>())

  const missingData: MissingDataRow[] = (data?.data ?? []).flatMap(patient => {
    const patientFindings = findingsByPatientId.get(patient.id) ?? []
    if (patientFindings.length === 0) {
      return []
    }

    return [
      {
        id: patient.id,
        patient,
        findings: patientFindings,
      },
    ]
  })

  const columns = useColumnDef<MissingDataRow>(
    () => [
      {
        key: 'patient.personal.lastName',
        title: <Trans>Patientenakte</Trans>,
        fixed: 'start',
        sorter: true,
        render: (_, record) => <FormattedPatientNameAndAddress patient={record.patient} />,
      },
      {
        key: 'findings.count',
        title: <Trans>Anzahl</Trans>,
        sorter: true,
        width: 120,
        render: (_, record) => record.findings.length,
      },
      {
        key: 'actions',
        title: <Trans>Aktionen</Trans>,
        fixed: 'end',
        sorter: false,
        render: (_, record) => (
          <ButtonLink to="/patienten/$id" params={{ id: record.id }} size="small">
            <Trans>zur Akte</Trans>
            <LinkIndicatorIcon />
          </ButtonLink>
        ),
      },
    ],
    [],
    useMissingDataQueryStore,
  )

  const handleTableChange: TableProps<MissingDataRow>['onChange'] = (pagination, filters, sorter) => {
    queryStore.onChange(
      {
        current: pagination.current ?? 1,
        pageSize: pagination.pageSize ?? queryStore.pageSize,
      },
      filters,
      sorter,
    )
  }

  usePageContainerOverride(
    {
      header: {
        extra: (
          <Space size="small">
            <SearchInput
              size="small"
              value={queryStore.meta.searchRawValue}
              onChange={e => queryStore.setSearch(e.target.value)}
            />
            <FilterButton activeFilterCount={queryStore.meta.activeFilterCount} onClick={() => setOpen(true)} />
          </Space>
        ),
      },
    },
    [queryStore.meta.searchRawValue, queryStore.setSearch, queryStore.meta.activeFilterCount, setOpen, queryStore],
  )

  return (
    <QueryFilterWrapper
      queryStore={useMissingDataQueryStore}
      open={open}
      onOpenChange={setOpen}
      filterConfig={[
        {
          name: 'patient.status.value',
          label: <Trans>Status der Patientenakte</Trans>,
          mode: 'multiple',
          options: PatientStatusEnum.$.options,
        },
      ]}
    >
      <Table
        dataSource={missingData}
        loading={isFetching || isFetchingFindings}
        columns={columns}
        rowKey="id"
        pagination={data}
        onChange={handleTableChange}
        size="small"
        scroll={{ x: 'max-content' }}
        expandable={{
          expandedRowRender: record => {
            const groupedFindings = groupFindings(record.patient.id, record.findings)

            return (
              <div className="space-y-3 px-4 py-2">
                {groupedFindings.map(row => {
                  const firstRoute = row.problems[0]?.route
                  const hasSameRouteForAllProblems = row.problems.every(problem => problem.route === firstRoute)

                  return (
                    <div key={row.key} className="border-neutral-200 border-b pb-2 last:border-b-0 last:pb-0">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <Typography.Text strong>
                            {row.section} ({row.count})
                          </Typography.Text>
                          <div className="mt-1 space-y-1">
                            {row.problems.map(problem =>
                              problem.route ? (
                                <Typography.Link key={problem.key} href={problem.route} className="block">
                                  {problem.message} <ArrowRightOutlined />
                                </Typography.Link>
                              ) : (
                                <Typography.Text key={problem.key} type="secondary" className="block">
                                  {problem.message}
                                </Typography.Text>
                              ),
                            )}
                          </div>
                        </div>
                        {firstRoute && hasSameRouteForAllProblems && (
                          <Typography.Link href={firstRoute}>
                            <Trans>Anzeigen</Trans>
                          </Typography.Link>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            )
          },
        }}
      />
    </QueryFilterWrapper>
  )
}
